import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import authService from "../services/auth";

// Step 1: send OTP to email
export const requestOtp = createAsyncThunk(
	"auth/requestOtp",
	async (email, { rejectWithValue }) => {
		try {
			const response = await authService.requestOtp(email);
			return response;
		} catch (error) {
			// Pass the raw i18n key back - the component translates and displays it
			const errorKey =
				error.originalMessage ||
				error.response?.data?.message ||
				error.message ||
				"AUTH.USER_NOT_FOUND";
			return rejectWithValue(errorKey);
		}
	},
);

// Step 2: verify OTP and get JWT
export const verifyOtp = createAsyncThunk(
	"auth/verifyOtp",
	async ({ email, code }, { rejectWithValue }) => {
		try {
			const response = await authService.verifyOtp(email, code);
			return response;
		} catch (error) {
			// Pass the raw i18n key back - the component translates and displays it
			const errorKey =
				error.originalMessage ||
				error.response?.data?.message ||
				error.message ||
				"AUTH.INVALID_OTP";
			return rejectWithValue(errorKey);
		}
	},
);

// Async thunk for fetching user profile
export const fetchUserProfile = createAsyncThunk(
	"auth/fetchUserProfile",
	async (_, { rejectWithValue }) => {
		try {
			const response = await authService.getProfile();
			return response;
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to fetch user profile",
			);
		}
	},
);

// Async thunk for user logout
export const logout = createAsyncThunk(
	"auth/logout",
	async (_, { rejectWithValue }) => {
		try {
			await authService.logout();
			return { success: true };
		} catch (error) {
			console.error("Logout failed:", error);
			return rejectWithValue(error.message || "Logout failed");
		}
	},
);

// Define initial state
const initialState = {
	user: null,
	isAuthenticated: false,
	redirectTo: null,
	loading: null,
	error: null,
};

// Create the auth slice
const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		clearAuth: (state) => {
			state.user = null;
			state.isAuthenticated = false;
			state.redirectTo = null;
			state.loading = null;
			state.error = null;
			localStorage.removeItem("selectedMenu");
			sessionStorage.clear();
			// Note: dlc_token is an httpOnly cookie - it cannot be cleared from JS.
			// The backend clears it via res.clearCookie("dlc_token") on /logout.
		},
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// requestOtp
			.addCase(requestOtp.pending, (state) => {
				state.loading = "requestOtp";
				state.error = null;
			})
			.addCase(requestOtp.fulfilled, (state) => {
				state.loading = null;
			})
			.addCase(requestOtp.rejected, (state, action) => {
				state.loading = null;
				state.error = action.payload;
			})
			// verifyOtp
			.addCase(verifyOtp.pending, (state) => {
				state.loading = "verifyOtp";
				state.error = null;
			})
			.addCase(verifyOtp.fulfilled, (state, action) => {
				state.loading = null;
				state.isAuthenticated = true;
				state.user = action.payload.user;
				state.redirectTo = action.payload.redirectTo ?? null;
				if (typeof window !== "undefined") {
					localStorage.setItem("selectedMenu", "projects");
				}
			})
			.addCase(verifyOtp.rejected, (state, action) => {
				state.loading = null;
				state.error = action.payload;
			})
			// Fetch user profile
			.addCase(fetchUserProfile.pending, (state) => {
				state.loading = "fetchProfile";
				state.error = null;
			})
			.addCase(fetchUserProfile.fulfilled, (state, action) => {
				state.loading = null;
				state.user = action.payload;
			})
			.addCase(fetchUserProfile.rejected, (state, action) => {
				state.loading = null;
				state.error = action.payload;
				// Any profile-fetch failure means the session is invalid - clear auth immediately
				// rather than waiting for the async logout() call dispatched by the 401 interceptor.
				state.isAuthenticated = false;
				state.user = null;
			})
			// Logout
			.addCase(logout.pending, (state) => {
				state.loading = "logout";
			})
			.addCase(logout.fulfilled, (state) => {
				authSlice.caseReducers.clearAuth(state);
				state.loading = null;
			})
			.addCase(logout.rejected, (state, action) => {
				authSlice.caseReducers.clearAuth(state);
				state.loading = null;
				state.error = action.payload;
			})
			// Reset transient UI state on rehydration
			.addCase(REHYDRATE, (state) => {
				state.loading = null;
				state.error = null;
			});
	},
});

// Export actions and reducer
export const { clearAuth, clearError } = authSlice.actions;
export default authSlice.reducer;
